import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  ClipboardCheck,
  MessageSquareText,
  ShieldCheck,
  Users,
  Wallet,
} from 'lucide-react';
import { ThemeToggle } from '../../app/theme';
import { useRevealOnScroll } from '../ui';
import { Footer } from './sections/Footer';

// The coach pitch is scoped to what is LIVE today: roster + consented
// visibility, assistant-drafted changes the coach approves, DM + scheduling,
// the client-side daily AI coach, AND on-platform package sales — clients buy
// coach session packages by card (Stripe checkout) and bookings draw down
// credits automatically. What we do NOT claim yet: automated coach payouts /
// self-serve package creation — that's the Connect track (twa-s55).

const yourBusiness = [
  {
    icon: Wallet,
    title: 'Your prices, paid on-platform',
    description:
      'Set your prices and sell session packages right on TrackWithAI — clients pay by card and their session credits track against bookings automatically. Prefer your own billing? That works too.',
  },
  {
    icon: CalendarClock,
    title: 'Your schedule',
    description:
      'Session booking runs on your availability and your rules — clients book the time you offer, not the other way around.',
  },
  {
    icon: ShieldCheck,
    title: 'Your programming, your call',
    description:
      'The assistant drafts plan adjustments from what your clients actually did. Nothing ships until you approve it.',
  },
  {
    icon: Users,
    title: 'Your client relationships',
    description:
      'Clients hear from you — your plans, your voice, your standards. Consent controls decide exactly what you see of their data.',
  },
];

const copilotValue = [
  {
    icon: Users,
    title: 'A roster that reads itself',
    description:
      'Every client, with the logs, check-ins, and trends they have consented to share — no chasing screenshots or spreadsheets.',
  },
  {
    icon: ClipboardCheck,
    title: 'Drafts, not homework',
    description:
      'When a client stalls, travels, or crushes a block, the assistant proposes the adjustment. You review, edit, and approve.',
  },
  {
    icon: MessageSquareText,
    title: 'One place to talk and plan',
    description:
      'Direct messaging and scheduling live next to the training data, so the conversation and the plan never drift apart.',
  },
];

const steps = [
  {
    number: '1',
    title: 'Apply in the app',
    description: 'Create an account and apply as a coach. We review every application and you hear back by email.',
  },
  {
    number: '2',
    title: 'Invite your clients',
    description: 'Send invites from your roster. Clients choose what to share — plans, workouts, nutrition — with consent controls.',
  },
  {
    number: '3',
    title: 'Coach with the copilot',
    description: 'Your clients get a daily AI coach that works from your plan. You get the picture, the drafts, and the final say.',
  },
];

function CoachNav() {
  return (
    <header className="sticky top-0 z-50 border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)] backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex min-h-[4.75rem] flex-wrap items-center justify-between gap-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,var(--color-brand),var(--color-brand-strong))] text-[color:var(--color-on-brand)] shadow-[0_16px_32px_rgba(var(--color-brand-rgb),0.22)]">
              <Activity className="w-6 h-6" />
            </div>
            <div className="leading-none">
              <p className="text-[0.68rem] font-extrabold uppercase tracking-[0.28em] text-[var(--color-muted)]">
                For Coaches
              </p>
              <span className="mt-1 block text-xl font-extrabold text-[var(--color-ink)]">TrackWithAI</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/" className="pill-nav hidden items-center gap-1.5 px-4 py-2 sm:inline-flex">
              <ArrowLeft className="h-4 w-4" />
              For everyone
            </Link>
            <ThemeToggle />
            <Link to="/login" className="pill-nav px-4 py-2">
              Sign In
            </Link>
            <Link to="/login" className="app-button app-button--primary px-4 py-2 text-sm">
              Apply as a coach
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

export function CoachLandingPage() {
  const businessRef = useRevealOnScroll<HTMLDivElement>();
  const copilotRef = useRevealOnScroll<HTMLDivElement>();
  const stepsRef = useRevealOnScroll<HTMLDivElement>();

  return (
    <div className="min-h-screen bg-[var(--color-app-bg)] text-[var(--color-ink)]">
      <CoachNav />

      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="section-eyebrow">Run your coaching business on TrackWithAI</p>
            <h1 className="display-serif text-5xl leading-[1.05] tracking-[-0.02em] text-[var(--color-ink)] sm:text-6xl">
              Keep the coaching.
              <br />
              Lose the admin.
            </h1>
            <p className="section-copy mx-auto mt-6 text-lg">
              Every one of your clients gets a daily AI coach that works from <em>your</em> plan —
              logging their food, tracking their lifts, keeping them honest between sessions. You
              get a copilot that watches the data, drafts the adjustments, and leaves every
              decision where it belongs: with you.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/login" className="app-button app-button--primary px-6 py-3">
                Apply as a coach
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/start" className="app-button app-button--secondary px-6 py-3">
                Try the client experience
              </Link>
            </div>
            <p className="mt-4 text-sm text-[var(--color-muted)]">
              We review every coach application — you&apos;ll hear back by email.
            </p>
          </div>
        </div>
      </section>

      {/* Your business stays yours */}
      <section className="py-20" style={{ background: 'var(--color-surface-muted)' }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <p className="section-eyebrow">The part that matters</p>
            <h2 className="section-title">Your business stays yours</h2>
            <p className="section-copy mx-auto">
              TrackWithAI is a copilot, not a marketplace. You are not renting clients from a
              platform — you are bringing your own business and getting hours of your week back.
            </p>
          </div>
          <div
            ref={businessRef}
            className="reveal-on-scroll reveal-stagger grid grid-cols-1 gap-8 md:grid-cols-2"
          >
            {yourBusiness.map((item) => (
              <div key={item.title} className="app-mini-panel h-full">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--color-brand-faint)] text-[var(--color-brand)]">
                  <item.icon className="h-6 w-6" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-[var(--color-ink)]">{item.title}</h3>
                <p className="text-[var(--color-muted)]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What the copilot does */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <p className="section-eyebrow">Your copilot</p>
            <h2 className="section-title">What you get on the other side of the table</h2>
            <p className="section-copy mx-auto">
              Spend your hours on judgment and relationships. The copilot handles the watching,
              the collating, and the first draft.
            </p>
          </div>
          <div
            ref={copilotRef}
            className="reveal-on-scroll reveal-stagger grid grid-cols-1 gap-8 md:grid-cols-3"
          >
            {copilotValue.map((item) => (
              <div key={item.title} className="app-mini-panel h-full">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--color-brand-faint)] text-[var(--color-brand)]">
                  <item.icon className="h-6 w-6" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-[var(--color-ink)]">{item.title}</h3>
                <p className="text-[var(--color-muted)]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20" style={{ background: 'var(--color-surface-muted)' }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <p className="section-eyebrow">Getting started</p>
            <h2 className="section-title">Three steps, no migration project</h2>
          </div>
          <div
            ref={stepsRef}
            className="reveal-on-scroll reveal-stagger mx-auto grid max-w-4xl grid-cols-1 gap-8 md:grid-cols-3"
          >
            {steps.map((step) => (
              <div key={step.number} className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[linear-gradient(135deg,var(--color-brand),var(--color-brand-strong))] text-lg font-extrabold text-[color:var(--color-on-brand)]">
                  {step.number}
                </div>
                <h3 className="mb-2 text-lg font-semibold text-[var(--color-ink)]">{step.title}</h3>
                <p className="text-[var(--color-muted)]">{step.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-14 text-center">
            <Link to="/login" className="app-button app-button--primary px-6 py-3">
              Apply as a coach
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
